// ─── src/ReportLeadsView.tsx ──────────────────────────────────────────────────
import { useState, useCallback } from "react";
import { Download, FileText, Search, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useReportLeads,
  useReportLeadStats,
  useDeleteReportLead,
  reportLeadKeys,
} from "./hooks/useReportLeads";
import { ReportLeadsTable } from "./components/dashboard/ReportLeadsTable";
import { Pagination }       from "./components/dashboard/Pagination";
import { TableSkeleton }    from "./components/ui/Skeleton";
import { StatCard }         from "./components/ui/StatCard";
import type { ReportLead }  from "./types/reportLead";

const LIMIT = 20;

const SLUG_LABEL: Record<string, string> = {
  "rising-from-the-rubble":                     "Rising From the Rubble",
  "covid-19-survival-guide":                    "COVID-19 Survival Guide",
  "entrepreneurship-in-exile":                  "Entrepreneurship in Exile",
  "entrepreneurship-in-conflict-zones":         "Entrepreneurship in Conflict Zones",
  "green-entrepreneurship-recovery":            "Green Entrepreneurship",
  "addressing-syria-entrepreneurship-skills-gap": "Syria Skills Gap",
};

function exportLeadsToCsv(rows: ReportLead[]) {
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = "First Name,Last Name,Email,Report Slug,Report Title,Downloaded At";
  const body   = rows.map((r) =>
    [r.firstName, r.lastName, r.email, r.reportSlug, r.reportTitle, r.createdAt]
      .map(escape)
      .join(",")
  );
  const csv  = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `report_leads_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportLeadsView() {
  const qc = useQueryClient();

  const [search,      setSearch]      = useState("");
  const [reportSlug,  setReportSlug]  = useState("");
  const [visualPage,  setVisualPage]  = useState(1);

  // Stats: total + per-report breakdown
  const { data: stats } = useReportLeadStats();

  // All rows for CSV export (unfiltered, high limit)
  const { data: allData } = useReportLeads({ page: 1, limit: 2000 });
  const allRows = allData?.data ?? [];

  // Table query: paginated + filtered
  const { data: tableData, isLoading, isFetching, isError } = useReportLeads({
    page:  visualPage,
    limit: LIMIT,
    ...(search     ? { search }     : {}),
    ...(reportSlug ? { reportSlug } : {}),
  });

  const { mutate: deleteReportLead, isPending: isDeleting } = useDeleteReportLead();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  // Report options derived from stats
  const reportOptions = Object.entries(stats?.byReport ?? {}).sort((a, b) => b[1] - a[1]);

  const handleSearch = useCallback((v: string) => {
    setSearch(v);
    setVisualPage(1);
  }, []);

  const handleReportFilter = useCallback((slug: string) => {
    setReportSlug(slug);
    setVisualPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: reportLeadKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <StatCard
          label="Total Downloads"
          value={stats?.total ?? 0}
          icon={FileText}
          accent="bg-brand-teal"
        />
        {reportOptions.map(([slug, count]) => (
          <StatCard
            key={slug}
            label={SLUG_LABEL[slug] ?? slug}
            value={count}
            icon={FileText}
            accent="bg-brand-orange"
          />
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border
                       rounded-lg text-white placeholder-brand-muted
                       focus:outline-none focus:border-brand-orange transition-colors"
          />
          {search && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Report filter */}
        <select
          value={reportSlug}
          onChange={(e) => handleReportFilter(e.target.value)}
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg
                     text-white focus:outline-none focus:border-brand-orange transition-colors"
        >
          <option value="">All Reports</option>
          {Object.keys(SLUG_LABEL).map((slug) => (
            <option key={slug} value={slug}>
              {SLUG_LABEL[slug]}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2 ml-auto">
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>

          {/* CSV Export */}
          <button
            onClick={() => exportLeadsToCsv(allRows)}
            disabled={allRows.length === 0}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load report leads. Check your API key or network.
        </div>
      ) : (
        <>
          <ReportLeadsTable
            rows={pageRows}
            onDelete={(id) => deleteReportLead(id)}
            isDeleting={isDeleting}
          />
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
